import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { queryClient } from '@/lib/query-client';
import { initObservability, trackPageview } from '@/lib/observability';
import { historyTrail } from '@/lib/history-trail';
import { routeTree } from './routeTree.gen';
import { RouteError } from '@/components/app/route-error';
import { RoutePending } from '@/components/app/route-pending';
import { NotFound } from '@/components/app/not-found';
import '@/styles/app.css';


const router = createRouter({
  routeTree,
  context: { queryClient },
  // Loaders read through the Query cache, so the router never caches on its own.
  defaultPreloadStaleTime: 0,
  defaultPreload: 'intent',
  defaultPendingMs: 400,
  defaultPendingMinMs: 300,
  defaultErrorComponent: RouteError,
  defaultPendingComponent: RoutePending,
  defaultNotFoundComponent: NotFound,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

router.subscribe('onResolved', ({ fromLocation, toLocation }) => {
  // An agency's upload link is named `/upload/:token` there (lib/observability masks it): its token never reaches analytics.
  trackPageview(toLocation.pathname);
  // Which page sits behind each history entry, so a way back can go Back (and keep that page's scroll).
  historyTrail.record(fromLocation, toLocation);
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

// After first paint: analytics and error reporting never block the entry chunk.
const idle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1));
idle(() => void initObservability(router));
